import axios from "axios";
import i18n from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // Cap request time so a stalled proxy/edge doesn't leave the button in
  // "Signing in…" indefinitely. Auth is CPU-bound (bcrypt) but async now,
  // so 20s is safely above p99 while still failing fast on network issues.
  timeout: 20000,
});

// Also attach Bearer token if present (fallback when cookies blocked)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("em_os_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach current UI language for content translation on GET requests
  const method = (config.method || "get").toLowerCase();
  if (method === "get") {
    const lang = (i18n.language || "nl").split("-")[0];
    config.params = { ...(config.params || {}), lang };
  }
  return config;
});

// Subscription-expired gate — the backend returns 402 with
// code=subscription_expired on protected endpoints once a yearly window lapses,
// and code=payment_required for users who never completed the paywall. Bounce
// them to /renew or /login accordingly so they can recover on their own.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    try {
      const status = error?.response?.status;
      const code = error?.response?.data?.detail?.code;
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      if (status === 402 && code === "subscription_expired" && !path.startsWith("/renew")) {
        window.location.assign("/renew");
      } else if (status === 402 && code === "payment_required" && !path.startsWith("/login") && !path.startsWith("/pay") && !path.startsWith("/signup")) {
        // User was logged in but never paid — wipe the session and send them
        // back to /login. The login endpoint will re-issue the payment token
        // and Login.jsx bounces to /pay/:token.
        localStorage.removeItem("em_os_token");
        window.location.assign("/login?reason=payment_required");
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
